import { RoutePackageDTO } from 'api/model';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  buildRebookingTimeline,
  formatDayHours,
  RebookingBar,
  RebookingBarTone,
  timelinePercent,
  timelineTicks,
} from './rebooking-helpers';
import { formatClock } from './selection-helpers';

const Details = styled.details`
  border-top: 1px dashed #b5b3b37f;
  padding-top: 6px;

  summary {
    cursor: pointer;
    padding: 2px 0;
    font-size: 12px;
    font-weight: 600;
    color: rgb(80, 80, 80);
  }
`;

const Gain = styled.span`
  color: rgb(29, 107, 56);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  align-items: center;
  gap: 4px 10px;
  padding: 8px 0 2px;
`;

const Axis = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: rgb(120, 120, 120);
  font-variant-numeric: tabular-nums;
`;

const RowLabel = styled.span<{ tone?: 'good' }>`
  font-size: 11px;
  font-weight: ${({ tone }) => (tone === 'good' ? 600 : 400)};
  color: ${({ tone }) => (tone === 'good' ? 'rgb(29, 107, 56)' : 'rgb(90, 90, 90)')};
`;

const Track = styled.div`
  position: relative;
  height: 12px;
  border-radius: 3px;
  background: rgb(241, 238, 234);
`;

const barColors: Record<RebookingBarTone, string> = {
  own: 'rgb(155, 161, 168)',
  donor: 'rgb(198, 155, 126)',
  moved: 'rgb(181, 84, 31)',
};

const Bar = styled.div<{ tone: RebookingBarTone }>`
  position: absolute;
  top: 0;
  bottom: 0;
  border-radius: 2px;
  background: ${({ tone }) => barColors[tone]};
`;

const Ghost = styled.div`
  position: absolute;
  top: -2px;
  bottom: -2px;
  border: 1.5px dashed rgb(204, 102, 51);
  border-radius: 2px;
  box-sizing: border-box;
`;

const Legend = styled.span`
  font-size: 11px;
  color: rgb(110, 110, 110);
`;

const toneLabel: Record<RebookingBarTone, string> = {
  own: '',
  donor: ' (från annat paket)',
  moved: ' (ny tid)',
};

/**
 * Den smala före/efter-bilden längst ner i paketet, ihopfälld som standard. Siffrorna i
 * hovringstexten är benens nummer i kortet och på kartan.
 */
const RebookingTimeline: React.FC<{ pkg: RoutePackageDTO }> = ({ pkg }) => {
  const timeline = useMemo(() => buildRebookingTimeline(pkg), [pkg]);
  if (!timeline) return null;

  const place = (start: number, end: number) => {
    const left = timelinePercent(start, timeline);
    return {
      left: `${left}%`,
      width: `${Math.max(0.8, timelinePercent(end, timeline) - left)}%`,
    };
  };

  const renderBars = (bars: RebookingBar[]) =>
    bars.map((b) => (
      <Bar
        key={b.assignmentId}
        style={place(b.start, b.end)}
        title={`Ben ${b.legNumber}${toneLabel[b.tone]}: ${formatClock(new Date(b.start))}–${formatClock(new Date(b.end))}`}
        tone={b.tone}
      />
    ));

  return (
    <Details>
      <summary>
        Dagen före och efter ombokningen ·{' '}
        <Gain>
          {formatDayHours(timeline.hoursBefore)} → {formatDayHours(timeline.hoursAfter)} h
        </Gain>
      </summary>
      <Grid>
        <span />
        <Axis>
          {timelineTicks(timeline).map((tick, i) => (
            // Samma klockslag kan stå två gånger när dagen går över ett dygn.
            // eslint-disable-next-line react/no-array-index-key
            <span key={i}>{tick}</span>
          ))}
        </Axis>
        <RowLabel>Nu</RowLabel>
        <Track>{renderBars(timeline.before)}</Track>
        <RowLabel tone="good">Om ja</RowLabel>
        <Track>
          {renderBars(timeline.after)}
          {timeline.ghosts.map((g) => (
            <Ghost key={`${g.start}-${g.end}`} style={place(g.start, g.end)} title="Bokad tid i dag" />
          ))}
        </Track>
        <span />
        <Legend>
          Grått = egna ben · beige = från annat paket · orange = ny tid · streckat = bokad tid i
          dag. Ungefärligt – benen ligger på planerade tider.
        </Legend>
      </Grid>
    </Details>
  );
};

export default RebookingTimeline;
